/**
 * Which rendering the `src/antd` seam serves.
 *
 * `'antd'` IS THE DEFAULT — the app ships the design it has always shipped,
 * and shadcn is OPT-IN. That was the position until 2026-08-25, was flipped to
 * shadcn that morning, and was flipped back the same day on the captain's
 * word: old design standard, new design available from the control. It is also
 * the answer this module gives from any unexpected state: an unreadable
 * localStorage, an unknown stored string, no window at all. Only an explicit,
 * persisted `setDesignMode('shadcn')` returns shadcn.
 *
 * There is ONE way in and one way back, and it is the same control: "New
 * design" / "Old design" under Theme in the account menu
 * (`./DesignMenuControl`). No URL parameter selects a mode, reveals a control
 * or forces a rendering — `?design` was removed with this flip.
 *
 * What opting in means on screen, stated rather than discovered: about a third
 * of the components in use are ported, so a page on "New design" is MIXED —
 * shadcn where a seam has been ported, antd everywhere else, on the same
 * screen. That is the known state of the port, not a bug report waiting to
 * happen.
 */
export type DesignMode = 'antd' | 'shadcn'

export const DESIGN_MODE_STORAGE_KEY = 'wh:design-mode'

const isDesignMode = (value: unknown): value is DesignMode =>
  value === 'antd' || value === 'shadcn'

/**
 * THE SHIPPED DEFAULT, and what every unusable read falls back to.
 *
 * This is the line a default flip is: move it and everyone who has never
 * touched the control moves with it, with nothing else to undo. It is
 * deliberately not shadowed by a test-only pin any more — the suite runs on
 * the shipped default because the shipped default is antd, which is the
 * rendering the repo's thousands of `.ant-*` assertions were written against.
 */
export const DEFAULT_DESIGN_MODE: DesignMode = 'antd'

const readStored = (): DesignMode => {
  try {
    const stored = window.localStorage.getItem(DESIGN_MODE_STORAGE_KEY)
    return isDesignMode(stored) ? stored : DEFAULT_DESIGN_MODE
  } catch {
    // Private-mode Safari and blocked third-party storage both throw here.
    return DEFAULT_DESIGN_MODE
  }
}

/**
 * The chosen rendering. Persisted, so it survives a reload and every in-app
 * navigation — which is the whole mechanism now that no URL carries the
 * choice: it is picked once, under Theme, and it stays picked.
 */
let mode: DesignMode =
  typeof window === 'undefined' ? DEFAULT_DESIGN_MODE : readStored()

/** What the seam should render. */
export const getDesignMode = (): DesignMode => mode

const listeners = new Set<() => void>()

export const setDesignMode = (next: DesignMode): void => {
  if (next === mode) return
  mode = next
  try {
    window.localStorage.setItem(DESIGN_MODE_STORAGE_KEY, next)
  } catch {
    // Persistence is best-effort; the in-memory choice still applies for this
    // page's lifetime.
  }
  listeners.forEach((listener) => listener())
}

/**
 * Re-read the persisted choice. Used by the cross-tab `storage` listener and
 * by tests that write localStorage directly.
 */
export const refreshDesignModeFromStorage = (): void => {
  const next = readStored()
  if (next === mode) return
  mode = next
  listeners.forEach((listener) => listener())
}

/**
 * Wire the one source that can change the answer without anyone calling
 * `setDesignMode`: a second tab flipping the toggle.
 *
 * Lazily, on the first subscriber, not at module load: this module sits on the
 * boot-static graph via the `src/antd/Button` seam leaf, and a module that
 * attaches window listeners on import is a side effect on that graph.
 */
let wired = false

const wireOnce = () => {
  if (wired || typeof window === 'undefined') return
  wired = true
  window.addEventListener('storage', (event) => {
    if (event.key === DESIGN_MODE_STORAGE_KEY) refreshDesignModeFromStorage()
  })
}

export const subscribeDesignMode = (listener: () => void): (() => void) => {
  wireOnce()
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

/** Test seam: drop every subscriber and re-read storage. Not used by the app. */
export const resetDesignModeForTests = (): void => {
  listeners.clear()
  mode = typeof window === 'undefined' ? DEFAULT_DESIGN_MODE : readStored()
}
